//*Libraries imports
import React, { useState, useRef } from "react";
import { CopyIcon, CheckCircle2, Eye, RefreshCw, X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate, useOutletContext, useLocation } from "react-router-dom";

//*Components Imports
import { PaymentInnerHeader } from "./components/shared/PaymentInnerHeader";
import { SalesContractArea } from "./components/shared/SalesContractArea";
import { DeleteReceiptModal } from "./components/ted/DeleteReceiptModal";
import { PaymentSuccessModal } from "./components/shared/PaymentSuccessModal";
import { ProductInfo } from "./components/shared/ProductInfo";
import { FilePreviewModal } from "./components/ted/FilePreviewModal";
import { ReceiptUploadArea } from "./components/ted/ReceiptUploadArea";

//*Hooks Imports
import { useNotification } from "@hooks/useNotification";
import { useCreatePayment } from "@/hooks/asaas/payments/mutations/useCreatePayment";

//*Types Imports
import type { ISaleOutputDTO } from "@/schemas/sales";

//*Hooks Imports
import { useUploadSaleReceipt } from "@/hooks/sales/mutations/useUploadSaleReceipt";

//*Utils Imports
import { copyToClipboard } from "@/utils/clipboardUtils";

//* Helpers Imports
import { calculatePaymentValues } from "@helpers/paymentCalculator";
import { MethodPaymentEnum } from "@/schemas/methodpayment";

//* Utils Imports
import { isSalePaid } from "./components/shared/utils";

const tedSchema = z.object({
  receipt: z.instanceof(File, { message: "O comprovante é obrigatório." }),
});

type TedFormData = {
  receipt: File;
};

const BANK_FIELDS = [
  { label: "Banco", value: "Banco Alfa S.A. (Código 325)" },
  { label: "Agência", value: "0458-2" },
  { label: "Conta", value: "17854-9 (Conta Corrente)" },
  { label: "Razão Social", value: "LegalTech Consultoria Jurídica Ltda" },
  { label: "CNPJ", value: "12.345.678/0001-90" },
];

export function PaymentTed() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sale } = useOutletContext<{ sale: ISaleOutputDTO | null }>();
  const notification = useNotification();
  const { mutate: uploadReceipt } = useUploadSaleReceipt();
  const mutateCreatePayment = useCreatePayment();

  const customerId = location.state?.customerId;
  const existingReceiptUrl = sale?.paymentDetails?.receiptUrl;

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TedFormData>({
    resolver: zodResolver(tedSchema),
    defaultValues: {
      receipt: undefined,
    },
  });

  const selectedFile = watch("receipt");

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [sendInProgress, setSendInProgress] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [previewModalVisible, setPreviewModalVisible] =
    useState<boolean>(false);
  const [successModalVisible, setSuccessModalVisible] =
    useState<boolean>(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [isReuploading, setIsReuploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPaid = isSalePaid(sale);

  React.useEffect(() => {
    if (isPaid) {
      setSuccessModalVisible(true);
    }
  }, [isPaid]);

  React.useEffect(() => {
    if (existingReceiptUrl) {
      setIsReuploading(false);
    }
  }, [existingReceiptUrl]);

  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleCopy = async (text: string, label?: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopyFeedback(label ? `Copiado: ${label}` : "Dados copiados!");
      setTimeout(() => setCopyFeedback(null), 2000);
    }
  };

  const handleCopyAll = () => {
    const allText = BANK_FIELDS.map((f) => `${f.label}: ${f.value}`).join("\n");
    handleCopy(allText);
  };

  const handleFileSelect = (file: File) => {
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const allowedExtensions = ["jpg", "jpeg", "png", "pdf"];

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.includes(fileExtension || "")
    ) {
      alert("Por favor, selecione apenas arquivos PDF, JPG, JPEG ou PNG.");
      return;
    }

    setValue("receipt", file, { shouldValidate: true });
    setUploadInProgress(true);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));

    setTimeout(() => {
      setUploadInProgress(false);
    }, 1500);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setValue("receipt", undefined as unknown as File);
    setPreviewUrl(null);
    setDeleteModalVisible(false);
  };

  const handleViewExistingReceipt = () => {
    if (existingReceiptUrl) {
      setPreviewUrl(existingReceiptUrl);
      setPreviewModalVisible(true);
    }
  };

  const getFileNameFromUrl = (url: string) => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const pathname = new URL(decodedUrl).pathname;
      return pathname.split("/").pop() || "comprovante";
    } catch {
      return "comprovante";
    }
  };

  const handleReupload = () => {
    setIsReuploading(true);
    setValue("receipt", undefined as unknown as File);
    setPreviewUrl(null);
  };

  const handleCancelReupload = () => {
    setIsReuploading(false);
    setValue("receipt", undefined as unknown as File);
    setPreviewUrl(null);
  };

  const onSubmit = (data: TedFormData) => {
    if (!sale?.id || !customerId) {
      notification.showError(
        "Informações de venda ou cliente não encontradas.",
      );
      return;
    }

    setSendInProgress(true);

    uploadReceipt(
      {
        file: data.receipt,
        saleId: sale.id,
      },
      {
        onSuccess: (receiptUrl) => {
          const calc = calculatePaymentValues({
            sale,
            billingType: MethodPaymentEnum.TED,
          });

          mutateCreatePayment.mutate(
            {
              saleId: sale.id,
              customer: customerId,
              billingType: "TED",
              receiptUrl,
              description: `Pagamento via transferência bancária (TED) ${sale.orderNumber}`,
              value: calc.finalValue,
              installmentCount: 1,
              totalValue: calc.finalValue,
            },
            {
              onSuccess: () => {
                setSuccessModalVisible(true);
                setSendInProgress(false);
              },
              onError: (error: any) => {
                if (
                  error.code === "PaymentValueMismatch" ||
                  error.message?.includes("desatualizados")
                ) {
                  window.location.reload();
                  return;
                }
                console.error("Erro ao criar pagamento:", error);
                const message =
                  error instanceof Error ? error.message : "Erro desconhecido";
                notification.showError(
                  message || "Erro ao criar o pagamento. Tente novamente.",
                );
                setSendInProgress(false);
              },
            },
          );
        },
        onError: (error) => {
          console.error("Erro ao enviar comprovante:", error);
          const message =
            error instanceof Error ? error.message : "Erro desconhecido";
          notification.showError(
            message || "Erro ao enviar o comprovante. Tente novamente.",
          );
          setSendInProgress(false);
        },
      },
    );
  };

  const handleFinalize = () => {
    setSuccessModalVisible(false);
    navigate(-1);
  };

  const getProductName = (product: unknown) => {
    if (!product) return "Produto não encontrado";
    if (typeof product === "object" && product !== null && "name" in product) {
      return (product as { name: string }).name;
    }
    if (typeof product === "string") {
      const match = product.match(/name:\s*'([^']+)'/);
      return match ? match[1] : product;
    }
    return String(product);
  };

  const paymentValues = sale
    ? calculatePaymentValues({
        sale,
        billingType: MethodPaymentEnum.TED,
      })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center overflow-x-hidden">
      <div className="w-full max-w-[800px] flex flex-col bg-white shadow-sm min-h-screen relative">
        <PaymentInnerHeader title="TED / Transferência" />

        <div className="flex flex-col">
          <SalesContractArea />

          <div className="flex flex-col gap-8 px-4 md:px-6 pb-32">
            {isPaid ? (
              <div className="bg-[#FCFCFC] rounded-[18px] p-6 border border-[#EDF2F6] flex flex-col items-center gap-4 text-center">
                <div className="size-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
                  <CheckCircle2 className="size-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-clarotexto-201">
                  Pagamento confirmado!
                </h2>
                <p className="text-gray-500 max-w-sm">
                  O pagamento deste pedido já foi realizado com sucesso. Você
                  não precisa fazer mais nada.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-[#FCFCFC] rounded-[18px] p-4 border border-[#EDF2F6] flex flex-col gap-4">
                  <h2 className="text-lg font-bold text-clarotexto-201">
                    TED / Transferência
                  </h2>
                  <ProductInfo
                    title={
                      sale?.productId
                        ? getProductName(sale.productId)
                        : "Carregando..."
                    }
                    price={
                      paymentValues ? (
                        <div className="flex flex-col items-end">
                          {paymentValues.discountApplied && (
                            <span className="text-sm text-gray-400 line-through">
                              {new Intl.NumberFormat("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              }).format(paymentValues.originalValue)}
                            </span>
                          )}
                          <span className="text-xl font-bold text-claroprimaria-01">
                            {new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(paymentValues.finalValue)}
                          </span>
                          {paymentValues.discountApplied && (
                            <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full mt-1">
                              {paymentValues.discountApplied}%{" "}
                              {sale?.managerDiscount
                                ? "de desconto aplicado"
                                : "de desconto à vista"}
                            </span>
                          )}
                        </div>
                      ) : (
                        "..."
                      )
                    }
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-row items-center justify-between">
                    <h2 className="text-lg font-bold text-clarotexto-201">
                      Dados bancários
                    </h2>
                    <button
                      onClick={handleCopyAll}
                      className="text-claroprimaria-01 text-sm font-semibold hover:underline"
                    >
                      Copiar tudo
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {BANK_FIELDS.map((field, idx) => (
                      <div
                        key={idx}
                        className="bg-[#FCFCFC] border border-[#EDF2F6] rounded-xl p-4 flex flex-row items-center justify-between group hover:border-claroprimaria-01/30 transition-colors"
                      >
                        <div className="flex flex-col flex-1 mr-4">
                          <span className="text-xs text-gray-500 font-medium mb-1">
                            {field.label}
                          </span>
                          <span className="text-sm text-clarotexto-201 font-semibold break-all">
                            {field.value}
                          </span>
                        </div>
                        <button
                          onClick={() => handleCopy(field.value, field.label)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                          title="Copiar"
                        >
                          <CopyIcon className="size-5 text-gray-400 group-hover:text-claroprimaria-01" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {copyFeedback && (
                    <div className="text-center text-sm text-green-600 font-medium animate-fade-in">
                      {copyFeedback}
                    </div>
                  )}
                </div>

                {existingReceiptUrl && !isReuploading ? (
                  <div className="bg-[#FCFCFC] border border-[#EDF2F6] rounded-[18px] p-6 flex flex-col items-center gap-4 text-center">
                    <div className="size-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
                      <CheckCircle2 className="size-8 text-green-600" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <h3 className="text-lg font-bold text-clarotexto-201">
                        Comprovante enviado!
                      </h3>
                      <p className="text-sm text-gray-500 max-w-xs mx-auto">
                        Seu comprovante já foi enviado e está em análise. Você
                        será notificado assim que o pagamento for confirmado.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 w-full max-w-xs mt-2">
                      <button
                        onClick={handleViewExistingReceipt}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-clarobordas-01 text-clarotexto-201 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <Eye className="size-4" />
                        Visualizar comprovante
                      </button>
                      <button
                        onClick={handleReupload}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-claroprimaria-01 font-semibold text-sm hover:underline"
                      >
                        <RefreshCw className="size-4" />
                        Enviar novo comprovante
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {isReuploading && (
                      <button
                        onClick={handleCancelReupload}
                        className="self-start text-sm text-gray-500 hover:text-clarotexto-201 flex items-center gap-1.5 transition-colors mb-2"
                      >
                        <X className="size-4" />
                        Cancelar reenvio e manter anterior
                      </button>
                    )}
                    <ReceiptUploadArea
                      selectedFile={selectedFile}
                      previewUrl={previewUrl}
                      isDragOver={isDragOver}
                      uploadInProgress={uploadInProgress}
                      errors={errors}
                      fileInputRef={fileInputRef}
                      onDrop={onDrop}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onSelectClick={() => fileInputRef.current?.click()}
                      onFileSelect={(e) =>
                        e.target.files?.[0] &&
                        handleFileSelect(e.target.files[0])
                      }
                      onPreviewOpen={() => setPreviewModalVisible(true)}
                      onDeleteOpen={() => setDeleteModalVisible(true)}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {(!existingReceiptUrl || isReuploading) && (
          <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[800px] bg-white border-t border-gray-100 p-6 md:p-8 shadow-[0_-8px_30px_rgb(0,0,0,0.08)] z-20">
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={!selectedFile || uploadInProgress || sendInProgress}
              className="w-full h-[52px] bg-claroprimaria-01 text-white rounded-xl font-bold text-base hover:bg-claroprimaria-01/90 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-3 shadow-lg shadow-claroprimaria-01/20"
            >
              {sendInProgress ? (
                <>
                  <div className="size-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar comprovante"
              )}
            </button>
          </div>
        )}

        <DeleteReceiptModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={handleRemoveFile}
        />

        <FilePreviewModal
          isOpen={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewUrl}
          fileType={
            selectedFile?.type ||
            (previewUrl?.toLowerCase().endsWith(".pdf")
              ? "application/pdf"
              : "image/jpeg")
          }
          fileName={
            selectedFile?.name ||
            (previewUrl ? getFileNameFromUrl(previewUrl) : undefined)
          }
        />

        <PaymentSuccessModal
          isOpen={successModalVisible}
          onClose={handleFinalize}
          onFinalize={handleFinalize}
        />
      </div>
    </div>
  );
}
